import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Brain, LineChart, Shield, Database, Zap, Link, BarChart3, Bot, ArrowRight, Check } from "lucide-react";

const CoreFeatures = () => {
  const [hoveredFeature, setHoveredFeature] = useState(null);

  // Memoize static data
  const features = useMemo(() => [
    { 
      id: 1, 
      icon: <Database className="w-8 h-8" />, 
      title: "Smart Lead Capture", 
      desc: "mbed a simple widget on your site (works with WordPress, Shopify, or plain HTML). Grabs names, emails, and why they're here details automatically.", 
      color: "from-pink-500 to-rose-500",
      benefits: ["Full CRUD operations", "Bulk export", "Smart filtering"]
    },
    { 
      id: 2, 
      icon: <BarChart3 className="w-8 h-8" />, 
      title: "Ad Data Collection", 
      desc: "Import campaign data via API or CSV and unify your sources for accurate attribution.", 
      color: "from-orange-500 to-red-500",
      benefits: ["API & CSV import", "Multi-source sync", "Real-time updates"]
    },
    { 
      id: 3, 
      icon: <Brain className="w-8 h-8" />, 
      title: "AI Lead Scoring", 
      desc: "Leads get a 1-100 score based on behavior—like multiple page views (+points!) or ad match. Train it with your feedback for even better results over time.", 
      color: "from-blue-500 to-cyan-500",
      benefits: ["ML-powered scoring", "Feedback loops", "Auto-calibration"]
    },
    { 
      id: 4, 
      icon: <Bot className="w-8 h-8" />, 
      title: "Anomaly Detection", 
      desc: "Flags suspicious stuff, like fake bot sign-ups or unusual traffic bursts. Review and block them in one click—say goodbye to spam headaches.", 
      color: "from-purple-500 to-indigo-500",
      benefits: ["Bot detection", "Fraud prevention", "Pattern analysis"]
    },
    { 
      id: 5, 
      icon: <LineChart className="w-8 h-8" />, 
      title: "Campaign Attribution", 
      desc: "See exactly which ad (Meta, Google, TikTok) brought in the winners. Visualize it with pie charts and timelines", 
      color: "from-cyan-500 to-blue-500",
      benefits: ["UTM tracking", "Multi-touch attribution", "ROI analytics"]
    },
    { 
      id: 6, 
      icon: <Zap className="w-8 h-8" />, 
      title: "Dashboard & Analytics", 
      desc: "Quick-glance stats, Total leads today, top sources, anomaly alerts. Filter, search, and export to CSV for your reports.", 
      color: "from-green-500 to-emerald-500",
      benefits: ["Real-time KPIs", "Custom widgets", "White-label ready"]
    },
    { 
      id: 7, 
      icon: <Link className="w-8 h-8" />, 
      title: "Integrations", 
      desc: "Seamlessly connects to the tools you already use,Google Ads,Whatsapp,Hubspot,Meta Ads & more", 
      color: "from-yellow-500 to-orange-500",
      benefits: ["20+ integrations", "Webhook support", "API-first design"]
    },
    { 
      id: 8, 
      icon: <Shield className="w-8 h-8" />, 
      title: "Security", 
      desc: "Built with JWT authentication, rate limiting, and enterprise-grade encryption.", 
      color: "from-red-500 to-pink-500",
      benefits: ["JWT authentication", "Rate limiting", "End-to-end encryption"]
    },
  ], []);

  // Respect reduced motion
  const prefersReducedMotion = useMemo(() => 
    window.matchMedia('(prefers-reduced-motion: reduce)').matches, []
  );

  const containerVariants = {
    hidden: prefersReducedMotion ? { opacity: 1 } : { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: prefersReducedMotion ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 40, scale: 0.92 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.7, type: "spring", stiffness: 100 },
    },
  };

  return (
    <section id="features" className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 py-24">
      {/* Animated mesh gradient background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-pink-500/15 rounded-full blur-3xl [animation:css-pulse 4s ease-in-out infinite] [animation-delay:0s]" />
        <div className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-cyan-500/15 rounded-full blur-3xl [animation:css-pulse 4s ease-in-out infinite] [animation-delay:1s]" />
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl [animation:css-pulse 4s ease-in-out infinite] [animation-delay:2s]" />
      </div>

      {/* Static grid background */}
      <div 
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
          backgroundSize: '50px 50px',
          transform: 'translateZ(0)',
        }}
      />

      {/* Floating particles - CSS anims */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className={`absolute w-1 h-1 rounded-full ${i % 3 === 0 ? 'bg-gradient-to-br from-pink-500/50' : i % 3 === 1 ? 'bg-gradient-to-br from-purple-500/50' : 'bg-gradient-to-br from-cyan-500/50'}`}
            style={{
              left: `${(i * 8) + 5}%`,
              top: `${Math.random() * 80 + 10}%`,
              animationDuration: `${3 + i * 0.2}s`,
              animationDelay: `${i * 0.15}s`,
              transform: 'translateZ(0)',
            }}
          />
        ))}
      </div>

      {/* Connection lines - CSS anims */}
      <svg className="absolute inset-0 w-full h-full opacity-10 pointer-events-none">
        <defs>
          <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#ec4899" />
            <stop offset="50%" stopColor="#8b5cf6" />
            <stop offset="100%" stopColor="#06b6d4" />
          </linearGradient>
        </defs>
        <line x1="5%" y1="15%" x2="95%" y2="25%" stroke="url(#lineGradient)" strokeWidth="1" className="[animation:css-line-fade 4s ease-in-out infinite]" />
        <line x1="10%" y1="75%" x2="90%" y2="65%" stroke="url(#lineGradient)" strokeWidth="1" className="[animation:css-line-fade 4s ease-in-out infinite] [animation-delay:1.5s]" />
        <line x1="15%" y1="50%" x2="85%" y2="55%" stroke="url(#lineGradient)" strokeWidth="1" className="[animation:css-line-fade 4s ease-in-out infinite] [animation-delay:3s]" />
      </svg>

      <div className="relative container mx-auto px-6 max-w-7xl">
        {/* Header */}
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
          animate={prefersReducedMotion ? false : { opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.9 }}
            animate={prefersReducedMotion ? false : { opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6"
          >
            <span className="text-sm font-medium text-white/90">Superpowers Activated</span>
          </motion.div>

          <h2 className="text-5xl md:text-7xl font-black mb-6 text-white leading-tight">
            Built For You<br />
            <span className="bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500 bg-clip-text text-transparent animate-gradient">
              Tailored for Real World Hustle
            </span>
          </h2>
          
          <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
           Results? More real deals, less busywork. Perfect for Global booming online markets, from Africa's tech hubs to Europe's e-commerce boom.
          </p>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-20"
        >
          {features.map((feature, i) => (
            <motion.div
              key={feature.id}
              variants={itemVariants}
              whileHover={prefersReducedMotion ? false : { y: -12, scale: 1.03 }}
              onHoverStart={() => setHoveredFeature(feature.id)}
              onHoverEnd={() => setHoveredFeature(null)}
              className="group relative will-change-transform"
              style={{ transform: 'translateZ(0)' }}
            >
              {/* Card background */}
              <div className="relative h-full bg-white/5 backdrop-blur-md rounded-2xl p-8 border border-white/10 hover:border-white/20 transition-all duration-300 overflow-hidden flex flex-col">
                
                {/* Gradient overlay on hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300 rounded-2xl`} />

                {/* Background glow */}
                <motion.div
                  className={`absolute inset-0 bg-gradient-to-r ${feature.color} opacity-0 group-hover:opacity-5 blur-xl transition-opacity duration-300 rounded-2xl will-change-transform`}
                  style={{ transform: 'translateZ(0)' }}
                  animate={prefersReducedMotion ? false : { scale: [1, 1.05, 1] }}
                  transition={{ duration: 2.5, repeat: Infinity, delay: i * 0.1 }}
                />

                <div className="relative z-10 flex flex-col h-full">
                  {/* Icon Container */}
                  <motion.div
                    className={`relative mb-6 w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center shadow-lg will-change-transform`}
                    style={{ transform: 'translateZ(0)' }}
                    whileHover={prefersReducedMotion ? false : { rotate: 360, scale: 1.15 }}
                    transition={{ duration: 0.6, type: "spring", stiffness: 400 }}
                  >
                    <div className="text-white">
                      {feature.icon}
                    </div>
                  </motion.div>

                  {/* Content */}
                  <h3 className="text-xl font-bold mb-3 text-white">
                    {feature.title}
                  </h3>
                  <p className="text-slate-300 leading-relaxed text-sm mb-6 flex-grow">
                    {feature.desc}
                  </p>

                  {/* Benefits list - shows on hover */}
                  <AnimatePresence>
                    {hoveredFeature === feature.id && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="space-y-2 mb-4"
                      >
                        {feature.benefits.map((benefit, bIdx) => (
                          <motion.div
                            key={bIdx}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: bIdx * 0.05 }}
                            className="flex items-center gap-2"
                          >
                            <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-br ${feature.color}`} />
                            <span className="text-slate-400 text-xs">{benefit}</span>
                          </motion.div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Learn more link */}
                  <motion.div
                    className="flex items-center gap-2 text-slate-400 group-hover:text-slate-300 text-sm font-medium cursor-pointer"
                    whileHover={prefersReducedMotion ? false : { x: 3 }}
                  >
                    <span>Learn more</span>
                    <ArrowRight className="w-4 h-4" />
                  </motion.div>
                </div>

                {/* Hover glow trail at bottom */}
                <motion.div
                  className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${feature.color} opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-b-2xl`}
                />

                {/* Corner accent pulse */}
                <motion.div
                  className={`absolute -top-2 -right-2 w-16 h-16 bg-gradient-to-br ${feature.color} rounded-full opacity-0 group-hover:opacity-20 blur-2xl transition-opacity duration-300 will-change-transform`}
                  style={{ transform: 'translateZ(0)' }}
                  animate={prefersReducedMotion ? false : { scale: [1, 1.3, 1] }}
                  transition={{ duration: 2, repeat: Infinity, delay: i * 0.15 }}
                />
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Stats Bar */}
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 30 }}
          animate={prefersReducedMotion ? false : { opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mb-20"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { value: "99.9%", label: "Uptime SLA" },
              { value: "<3s", label: "Load Times" },
              { value: "1000+", label: "Leads/Hour" },
              { value: "20+", label: "Integrations" }
            ].map((stat, idx) => (
              <motion.div
                key={idx}
                initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.9 }}
                animate={prefersReducedMotion ? false : { opacity: 1, scale: 1 }}
                transition={{ delay: idx * 0.1 }}
                whileHover={prefersReducedMotion ? false : { y: -5, scale: 1.05 }}
                className="relative bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-all overflow-hidden group will-change-transform"
                style={{ transform: 'translateZ(0)' }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-pink-500/5 via-purple-500/5 to-cyan-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="relative z-10 text-center">
                  <div className="text-3xl md:text-4xl font-black bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500 bg-clip-text text-transparent mb-2">
                    {stat.value}
                  </div>
                  <div className="text-slate-400 text-sm font-medium">{stat.label}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 30 }}
          animate={prefersReducedMotion ? false : { opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-center"
        >
          <p className="text-slate-400 mb-8 text-lg">
            All superpowers work together seamlessly. Ready to unleash them?
          </p>
      
        </motion.div>
      </div>

      <style>{`
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .animate-gradient {
          background-size: 200% auto;
          animation: gradient 3s linear infinite;
        }
        @keyframes css-pulse {
          0%, 100% { opacity: 0.15; transform: scale(1); }
          50% { opacity: 0.3; transform: scale(1.05); }
        }
        @keyframes css-line-fade {
          0%, 100% { opacity: 0.2; }
          50% { opacity: 0.5; }
        }
        @keyframes particle-float {
          0%, 100% { 
            transform: translateY(0) scale(1) opacity: 0.3; 
          }
          50% { 
            transform: translateY(-30px) scale(2) opacity: 0.8; 
          }
        }
        .particle-float {
          animation: particle-float ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default CoreFeatures;